import type { DayCalendar, WorkerStatus } from "../api/types";
import { avatarColor, getInitials } from "./session";

const WORKER_URN_PREFIX = "urn:senacle:worker:";

/** Une liste d'assignations vide ne dit rien tant qu'on ignore si la réponse est
 * arrivée : « pas encore su », « impossible à savoir » et « su qu'il n'y en a
 * pas » mènent à trois affichages opposés — patienter, signaler, proposer
 * d'affecter. L'état est porté par tâche, pas par lot : une requête en vol ne
 * doit pas rendre incertaines les tâches déjà résolues. */
export type AssignmentState = "pending" | "error" | "ready";

export interface TaskAssignments<T> {
  items: T[];
  state: AssignmentState;
}

export function assignmentState(result: {
  isPending: boolean;
  isError: boolean;
}): AssignmentState {
  if (result.isPending) return "pending";
  if (result.isError) return "error";
  return "ready";
}

/** Réconcilie les deux espaces d'identifiants côté équipe : LTE target_ref porte
 * un worker_url, TM actor_ref est un TEXT opaque (worker_url *ou* username,
 * cf. api/tmTasks.ts). On ramène tout à la forme URN — miroir exact de
 * workerURLFromUsername (time-and-attendance/internal/handlers/manager.go). */
export function normalizeWorkerRef(ref: string): string {
  const r = ref.trim();
  return r.startsWith(WORKER_URN_PREFIX) ? r : `${WORKER_URN_PREFIX}${r}`;
}

export function findWorker(
  workers: WorkerStatus[],
  ref: string | undefined,
): WorkerStatus | undefined {
  if (!ref) return undefined;
  const urn = normalizeWorkerRef(ref);
  return workers.find((w) => w.worker_url === urn);
}

/** display_name est nullable dès que Keycloak est injoignable (le handler Go
 * dégrade en silence) : repli sur l'identifiant de l'URN, le même pour toutes
 * les vues équipe (onglets, panneau semaine, revue des feuilles). */
export function workerLabel(
  w: Pick<WorkerStatus, "worker_url" | "display_name">,
): string {
  return w.display_name || w.worker_url.split(":").pop() || w.worker_url;
}

/** Tri par nom affiché. L'ordre de /manager/team n'est pas garanti : sans tri,
 * les lignes sautaient à chaque rafraîchissement de l'équipe. */
export function sortByWorkerLabel<
  T extends Pick<WorkerStatus, "worker_url" | "display_name">,
>(workers: T[]): T[] {
  return [...workers].sort((a, b) =>
    workerLabel(a).localeCompare(workerLabel(b), "fr"),
  );
}

/** Heures pointées sur la semaine, telles que les rend le calendrier manager. */
export function formatWeekHours(days: DayCalendar[]): string {
  return `${days.reduce((sum, d) => sum + d.total_hours, 0).toFixed(1)}h`;
}

export interface WorkerBadge {
  label: string;
  initials: string;
  color: string;
}

export function workerBadge(w: WorkerStatus): WorkerBadge {
  const label = workerLabel(w);
  return {
    label,
    initials: getInitials(label) || "?",
    color: avatarColor(w.worker_url),
  };
}
